import { useState } from 'react';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { formatDateOnly } from '../../../common/utils/date';
import { useMedicalEventsList, useCreateMedicalEvent } from '../contexts/medical';

import './styles/events.css';

// Log de salud (admin-medical-events.md) para un sujeto: animal (donadora/toro) XOR
// receptora. Lista los eventos + alta. Las pérdidas (abortion/embryonic_loss) se
// registran por el flujo de dx/aborto (necesitan preñez) → acá solo tratamiento/
// observación/otro. Sin PATCH/DELETE en el contrato.
const TYPE_LABEL = {
    embryonic_loss: 'Pérdida embrionaria',
    abortion:       'Aborto',
    treatment:      'Tratamiento',
    observation:    'Observación',
    other:          'Otro',
};
const ADD_TYPES = [
    { value: 'treatment',   label: 'Tratamiento' },
    { value: 'observation', label: 'Observación' },
    { value: 'other',       label: 'Otro' },
];
const today = () => new Date().toLocaleDateString('en-CA');

function detailText(details) {
    if (!details) return '';
    if (typeof details === 'string') return details;
    return details.note || details.source || '';
}

export default function MedicalEventsPanel({ animalId, recipientId }) {
    const subject = animalId ? { animal_id: Number(animalId) } : { recipient_id: Number(recipientId) };
    const { data, isLoading, error } = useMedicalEventsList({ ...subject, limit: 100 });
    const events = data?.items || [];
    const { mutateAsync: create, isPending } = useCreateMedicalEvent();

    const [adding, setAdding] = useState(false);
    const [type, setType] = useState('treatment');
    const [date, setDate] = useState(today());
    const [note, setNote] = useState('');

    const submit = async () => {
        if (!date) { toast.error('Elegí la fecha'); return; }
        const payload = { ...subject, type, date };
        if (note.trim()) payload.details = { note: note.trim() };
        try {
            await create(payload);
            toast.success('Evento registrado');
            setAdding(false); setType('treatment'); setDate(today()); setNote('');
        } catch (er) {
            if (er?.status !== 403) toast.error(er?.message || 'No se pudo registrar el evento');
        }
    };

    return (
        <section className="med-panel">
            <div className="med-panel__head">
                <h2 className="med-panel__title">Salud (eventos médicos)</h2>
                <Gate domain="medical_events" action="write">
                    {!adding && <button type="button" className="med-panel__add" onClick={() => setAdding(true)}>Registrar evento</button>}
                </Gate>
            </div>

            {adding && (
                <div className="med-form">
                    <label className="med-field">
                        <span className="med-field__label">Tipo</span>
                        <select value={type} onChange={(e) => setType(e.target.value)} disabled={isPending}>
                            {ADD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                    </label>
                    <label className="med-field">
                        <span className="med-field__label">Fecha</span>
                        <input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} disabled={isPending} />
                    </label>
                    <label className="med-field med-field--grow">
                        <span className="med-field__label">Nota</span>
                        <input type="text" value={note} onChange={(e) => setNote(e.target.value)} disabled={isPending} placeholder="Opcional (dosis, observación…)" />
                    </label>
                    <div className="med-form__actions">
                        <button type="button" className="med-btn med-btn--ghost" onClick={() => setAdding(false)} disabled={isPending}>Cancelar</button>
                        <button type="button" className="med-btn med-btn--primary" onClick={submit} disabled={isPending}>{isPending ? 'Guardando…' : 'Guardar'}</button>
                    </div>
                </div>
            )}

            {error ? (
                <p className="med-panel__muted">{error.status === 403 ? 'Sin permiso para ver el log de salud.' : 'No se pudieron cargar los eventos.'}</p>
            ) : isLoading ? (
                <p className="med-panel__muted">Cargando…</p>
            ) : !events.length ? (
                <p className="med-panel__muted">Sin eventos médicos.</p>
            ) : (
                <ul className="med-list">
                    {events.map((e) => (
                        <li key={e.id} className="med-item">
                            <span className={`med-type med-type--${e.type}`}>{TYPE_LABEL[e.type] || e.type}</span>
                            <span className="med-item__date">{formatDateOnly(e.date)}</span>
                            {detailText(e.details) && <span className="med-item__note">{detailText(e.details)}</span>}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}
